import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';

import { ResolvedShare } from '@bimdata/definitions/dto';
import { RequestStatus } from '@/contracts';

import { RootState, useAppSelector } from '@/state/store';

import { loginByShare } from '@/api/auth';
import { item, close } from  '@/loaders/project';
import { handleError } from '@/actions/common';

import Title from '@/components/Title';

import ProjectViewer from './ProjectViewer';
import Banner from '@/components/Banner';
import PageTitle from '@/components/PageTitle';
import Layout from '../Layout';
import CenteringWrapper from '@/components/CenteringWrapper';
import t from '@/utils/translate';
import { ApiError } from '@bimdata/client';
import { ModelData } from '@/state/types/model-data';

const AccessDeniedBanner = styled('div')({
  fontWeight: 400,
  fontSize: '17px',
  color: '#6B778C',
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  alignContent: 'center',
  justifyContent: 'center',
  alignItems: 'center',

  '& strong': {
    fontSize: '28px',
    fontWeight: 400,
  }
});

const modelValidator = (m: ModelData) => !m.failed && m.ready && !!m.elements;

export default () => {
  const { code } = useParams();
  const [share, setShare] = useState<ResolvedShare | null | false>(null);
  
  const nav = useNavigate();
  
  const { current, status } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.current,
        (state: RootState) => state.projects.openingStatus,
      ],
      (current, status) => ({
        current,
        status,
      })
    )
  );

  useEffect(() => {
    if (code) {
      loginByShare(code)
        .then(setShare)
        .catch((error) => {
          if (error instanceof ApiError && error.code === 403) {
            return setShare(false);
          }
          handleError(error);
        });
    }
  }, [ code ]);

  useEffect(() => {
    if (share === false) {
      window.setTimeout(() => {
        nav('/');
      }, 5000);
    }
  }, [ share ]);

  useEffect(() => {
    if (share) {
      if (!current) {
        item(share.item.id, share.access);
      } else {
        return close;
      }
    }
  }, [share, current]);

  const loaded = useMemo(() => {
    return share
      && current
      && current.code === share.item.id
      && status !== RequestStatus.ERROR;
  }, [ current, status, share ]);

  if (share === false) {
    return <Layout>
      <AccessDeniedBanner>
        {t('project.message.share.accessDenied', true)}
      </AccessDeniedBanner>
    </Layout>;
  }

  const hasData = current?.models?.filter((s) => !!s.models.filter(modelValidator).length).length || false;

  return <Layout>
    {!!current && (<Title id="title" style={{ backgroundColor: '#ffffff' }}>{ current?.name }</Title>)}
    <PageTitle>{current && `${current?.name} — BIMDATA`}</PageTitle>
    { status === RequestStatus.ERROR && (<CenteringWrapper top={60}><Banner>{t('project.message.failedToLoad', true)}</Banner></CenteringWrapper>)}
    {loaded && current && (
      (hasData)
      ? (<ProjectViewer accessToken={share!.access} modelValidator={modelValidator} top={60} project={current}/>)
      : (<CenteringWrapper top={60}><Banner>{t('project.message.empty', true)}</Banner></CenteringWrapper>)
    )}
  </Layout>;
};